import { supabase } from '../lib/supabase';
import { semanticJobSearch } from './semanticSearchService';
import { DeterministicSearchEngine } from '../lib/search_fallback';

// Initialize fallback search engine
const fallbackEngine = new DeterministicSearchEngine();
let isFallbackInitialized = false;

/**
 * Job Search Service
 * Integrates semantic AI search with deterministic fallback
 */

/**
 * Search jobs with filters - Enhanced with AI semantic search
 * @param {Object} filters - Search filters
 * @returns {Promise<Object>} Search results with metadata
 */
export const searchJobs = async (filters = {}) => {
  try {
    const {
      query = '',
      location = '',
      remoteType = '',
      employmentType = '',
      experienceLevel = '',
      salaryMin = null,
      sortBy = 'created_at',
      sortOrder = 'desc',
      limit = 50,
      offset = 0
    } = filters;

    // Build base query
    let queryBuilder = supabase?.from('jobs')?.select(`
        id,
        title,
        slug,
        description,
        requirements,
        employment_type,
        experience_level,
        salary_min,
        salary_max,
        salary_currency,
        remote_type,
        status,
        published_at,
        created_at,
        companies (
          id,
          name,
          slug,
          logo_url,
          industry,
          company_size
        ),
        locations (
          id,
          name,
          city,
          state,
          country,
          address
        )
      `, { count: 'exact' })?.eq('status', 'published');

    // Apply basic filters first
    if (location) {
      queryBuilder = queryBuilder?.or(
        `locations.city.ilike.%${location}%,locations.state.ilike.%${location}%,locations.country.ilike.%${location}%`
      );
    }

    if (remoteType) {
      queryBuilder = queryBuilder?.eq('remote_type', remoteType);
    }

    if (employmentType) {
      queryBuilder = queryBuilder?.eq('employment_type', employmentType);
    }

    if (experienceLevel) {
      queryBuilder = queryBuilder?.eq('experience_level', experienceLevel);
    }

    if (salaryMin) {
      queryBuilder = queryBuilder?.gte('salary_min', salaryMin);
    }

    // If no query, just apply filters and sort
    if (!query?.trim()) {
      queryBuilder = queryBuilder?.order(sortBy, { ascending: sortOrder === 'asc' });
      queryBuilder = queryBuilder?.range(offset, offset + limit - 1);

      const { data, error, count } = await queryBuilder;

      if (error) throw error;

      return {
        jobs: data || [],
        total: count || 0,
        hasMore: (offset + limit) < (count || 0),
        searchMode: 'filter-only'
      };
    }

    // Fetch all matching jobs for AI ranking
    const { data: allJobs, error, count } = await queryBuilder;

    if (error) throw error;

    // Try semantic search first
    try {
      const semanticResult = await semanticJobSearch(query, allJobs || [], {
        limit: limit,
        includeReasons: true
      });

      // Apply pagination to semantic results
      const paginatedResults = semanticResult?.results?.slice(offset, offset + limit);

      return {
        jobs: paginatedResults,
        total: semanticResult?.totalResults || 0,
        hasMore: (offset + limit) < (semanticResult?.totalResults || 0),
        searchMode: 'semantic-ai',
        searchIntent: semanticResult?.intent
      };
    } catch (semanticError) {
      console.warn('Semantic search unavailable, using fallback:', semanticError?.message);

      // Initialize fallback if needed
      if (!isFallbackInitialized && allJobs?.length > 0) {
        await fallbackEngine?.initialize(allJobs);
        isFallbackInitialized = true;
      }

      // Use deterministic fallback
      const fallbackResult = await fallbackEngine?.search(query, {
        limit,
        offset,
        filters: {
          remoteType,
          employmentType,
          experienceLevel,
          salaryMin
        }
      });

      return {
        jobs: fallbackResult?.results || [],
        total: fallbackResult?.totalResults || 0,
        hasMore: fallbackResult?.pagination?.hasMore || false,
        searchMode: 'deterministic-fallback',
        fallbackInfo: fallbackResult?.fallback
      };
    }
  } catch (error) {
    console.error('Error searching jobs:', error);
    return {
      jobs: [],
      total: 0,
      hasMore: false,
      searchMode: 'error',
      error: error?.message
    };
  }
};

/**
 * Get job by ID
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job details
 */
export const getJobById = async (jobId) => {
  try {
    const { data, error } = await supabase?.from('jobs')?.select(`
        *,
        companies (
          id,
          name,
          slug,
          description,
          logo_url,
          cover_image_url,
          industry,
          company_size,
          founded_year,
          website
        ),
        locations (
          id,
          name,
          address,
          city,
          state,
          country,
          postal_code,
          is_headquarters
        )
      `)?.eq('id', jobId)?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching job:', error);
    return null;
  }
};

/**
 * Get jobs by company ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Array of jobs
 */
export const getJobsByCompanyId = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('jobs')?.select(`
        id,
        title,
        slug,
        description,
        employment_type,
        experience_level,
        salary_min,
        salary_max,
        salary_currency,
        remote_type,
        status,
        published_at,
        locations (
          id,
          name,
          city,
          state,
          country
        )
      `)?.eq('company_id', companyId)?.eq('status', 'published')?.order('published_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching company jobs:', error);
    return [];
  }
};

/**
 * Get company by slug
 * @param {string} slug - Company slug
 * @returns {Promise<Object>} Company details
 */
export const getCompanyBySlug = async (slug) => {
  try {
    const { data, error } = await supabase?.from('companies')?.select(`
        *,
        locations (
          id,
          name,
          address,
          city,
          state,
          country,
          postal_code,
          is_headquarters
        )
      `)?.eq('slug', slug)?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching company:', error);
    return null;
  }
};

/**
 * Get locations by company ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Array of locations
 */
export const getLocationsByCompanyId = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('locations')?.select('*')?.eq('company_id', companyId)?.order('is_headquarters', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching company locations:', error);
    return [];
  }
};

export default {
  searchJobs,
  getJobById,
  getJobsByCompanyId,
  getCompanyBySlug,
  getLocationsByCompanyId
};